import React from 'react';
import { createPortal } from 'react-dom';
import { useDeviceLayout } from '../contexts/DeviceLayoutContext';
import { isStudentsBuild } from '../config/AppStatic';
import Button from './ui/Button';
import MultiSelectDropdown from './ui/MultiSelectDropdown'; // Keep this import
import { HiAdjustmentsHorizontal } from 'react-icons/hi2';
import MobileFilterButtons from './MobileFilterButtons';
import TeacherSelectionModal from './TeacherSelectionModal';
import { SCHEDULE_FILTER_ALL_GROUPS, isScheduleFilterAllGroupsOnly } from '../config/constants';

// Chevron icon matching MultiSelectDropdown
const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

function ScheduleFilter({
  // Filter options
  teachers,
  courses,
  groups, // Keep groups prop
  // Current filter values
  selectedTeacher,
  selectedCourse,
  selectedGroups, // Keep selectedGroups prop
  onTeacherChange,
  onCourseChange,
  onGroupChange, // Keep onGroupChange prop
  onClearFilters,
  isPublicView = false, // NEW: Add isPublicView prop
  onOpenComplexFilter, // NEW: Add complex filter handler
  // Complex filter data
  complexFilterGroups = [],
  complexFilterTeachers = [],
  complexFilterSubjects = [],
  complexFilterRooms = [],
  // Visibility controls
  showTeacher = true,
  showCourse = !isPublicView,
  showGroups = true,
  compactMobile = false,
  onOpenStudentGroupModal
}) {
  const { isMobileLayout } = useDeviceLayout();
  const [isTeacherModalOpen, setIsTeacherModalOpen] = React.useState(false);
  const [isTeacherDropdownOpen, setIsTeacherDropdownOpen] = React.useState(false);
  const [isGroupDropdownOpen, setIsGroupDropdownOpen] = React.useState(false);
  const [teacherSearchTerm, setTeacherSearchTerm] = React.useState('');
  const [groupSearchTerm, setGroupSearchTerm] = React.useState('');
  const [teacherPanelStyle, setTeacherPanelStyle] = React.useState({});
  const [groupPanelStyle, setGroupPanelStyle] = React.useState({});
  const teacherDropdownRef = React.useRef(null);
  const teacherTriggerRef = React.useRef(null);
  const groupDropdownRef = React.useRef(null);
  const groupTriggerRef = React.useRef(null);
  // Ensure callbacks are functions to prevent errors
  const handleTeacherChange = (e) => typeof onTeacherChange === 'function' && onTeacherChange(e.target.value);
  const handleCourseChange = (e) => typeof onCourseChange === 'function' && onCourseChange(e.target.value);

  // Group filter: single-select UIs pass a string; MultiSelectDropdown passes an array
  const handleGroupsChange = (value) => {
    if (typeof onGroupChange === 'function') {
      onGroupChange(value);
    }
  };

  const handleClearFilters = () => {
    setIsTeacherDropdownOpen(false);
    setIsGroupDropdownOpen(false);
    if (typeof onClearFilters === 'function') {
      onClearFilters();
    }
  };
  const handleOpenComplexFilter = () => typeof onOpenComplexFilter === 'function' && onOpenComplexFilter();

  const handleOpenStudentModal = () => typeof onOpenStudentGroupModal === 'function' && onOpenStudentGroupModal();
  const handleOpenTeacherModal = () => setIsTeacherModalOpen(true);
  const handleCloseTeacherModal = () => setIsTeacherModalOpen(false);

  const handleTeacherSelect = (selectedTeacher) => {
    if (typeof onTeacherChange === 'function') {
      onTeacherChange(selectedTeacher);
    }
  };
  const handleDesktopTeacherSelect = (teacherName) => {
    if (typeof onTeacherChange === 'function') {
      onTeacherChange(teacherName);
    }
    setIsTeacherDropdownOpen(false);
  };

  // Ensure options are arrays before mapping
  const teacherOptions = Array.isArray(teachers) ? teachers : [];
  const courseOptions = Array.isArray(courses) ? courses : [];
  const groupOptions = Array.isArray(groups) ? groups : [];

  // Check if complex filters are active
  const complexGroupFilterActive = complexFilterGroups.length > 0
    && !isScheduleFilterAllGroupsOnly(complexFilterGroups);
  const complexFiltersActive = complexGroupFilterActive ||
    complexFilterTeachers.length > 0 ||
    complexFilterSubjects.length > 0 ||
    complexFilterRooms.length > 0;

  // Helper function to generate complex filter text overview
  const getComplexFilterText = () => {
    const parts = [];

    if (complexGroupFilterActive) {
      const groupsText = complexFilterGroups.length > 3
        ? complexFilterGroups.slice(0, 3).join(', ') + '...'
        : complexFilterGroups.join(', ');
      parts.push(`Groups: ${groupsText}`);
    }
    if (complexFilterTeachers.length > 0) {
      const teachersText = complexFilterTeachers.length > 3
        ? complexFilterTeachers.slice(0, 3).join(', ') + '...'
        : complexFilterTeachers.join(', ');
      parts.push(`Teachers: ${teachersText}`);
    }
    if (complexFilterSubjects.length > 0) {
      const subjectsText = complexFilterSubjects.length > 3
        ? complexFilterSubjects.slice(0, 3).join(', ') + '...'
        : complexFilterSubjects.join(', ');
      parts.push(`Subjects: ${subjectsText}`);
    }
    if (complexFilterRooms.length > 0) {
      const roomsText = complexFilterRooms.length > 3
        ? complexFilterRooms.slice(0, 3).join(', ') + '...'
        : complexFilterRooms.join(', ');
      parts.push(`Rooms: ${roomsText}`);
    }

    return parts.join(' | ');
  }; // Keep group options
  const useSingleSelectGroupFilter = isPublicView || isStudentsBuild();
  const modernFilterButtonClass = 'w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/95 shadow-sm text-sm text-gray-700 min-h-[48px] transition focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 hover:border-gray-300 flex items-center justify-between gap-2 text-left';
  const modernReadonlyControlClass = 'w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/95 shadow-sm text-sm text-gray-700 min-h-[48px] flex items-center';
  const teacherNames = teacherOptions
    .filter(t => t)
    .map(teacher => typeof teacher === 'string' ? teacher : teacher?.name || 'Unknown');
  const filteredTeacherNames = teacherNames.filter((name) => {
    if (!teacherSearchTerm.trim()) {
      return true;
    }
    return name.toLowerCase().includes(teacherSearchTerm.trim().toLowerCase());
  });

  const groupNames = groupOptions
    .filter(g => g)
    .map(group => typeof group === 'string' ? group : group?.name || 'Unknown');
  const filteredGroupNames = groupNames.filter((name) => {
    if (!groupSearchTerm.trim()) {
      return true;
    }
    return name.toLowerCase().includes(groupSearchTerm.trim().toLowerCase());
  });

  const updateTeacherPanelPosition = React.useCallback(() => {
    if (!teacherTriggerRef.current) {
      return;
    }
    const rect = teacherTriggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const panelHeightEstimate = 280;
    const margin = 4;
    let top = 'auto';
    let bottom = 'auto';

    if (spaceBelow >= panelHeightEstimate || spaceBelow >= spaceAbove) {
      top = `${rect.bottom + window.scrollY + margin}px`;
    } else {
      bottom = `${window.innerHeight - rect.top - window.scrollY + margin}px`;
    }

    setTeacherPanelStyle({
      position: 'absolute',
      top,
      bottom,
      left: `${rect.left + window.scrollX}px`,
      width: `${rect.width}px`,
      zIndex: 1100
    });
  }, []);

  const updateGroupPanelPosition = React.useCallback(() => {
    if (!groupTriggerRef.current) {
      return;
    }
    const rect = groupTriggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const panelHeightEstimate = 280;
    const margin = 4;
    let top = 'auto';
    let bottom = 'auto';

    if (spaceBelow >= panelHeightEstimate || spaceBelow >= spaceAbove) {
      top = `${rect.bottom + window.scrollY + margin}px`;
    } else {
      bottom = `${window.innerHeight - rect.top - window.scrollY + margin}px`;
    }

    setGroupPanelStyle({
      position: 'absolute',
      top,
      bottom,
      left: `${rect.left + window.scrollX}px`,
      width: `${rect.width}px`,
      zIndex: 1100
    });
  }, []);

  React.useEffect(() => {
    if (!isTeacherDropdownOpen) {
      setTeacherSearchTerm('');
      return;
    }

    const handleClickOutside = (event) => {
      const panelElement = document.getElementById('teacher-filter-panel');
      if (
        teacherDropdownRef.current &&
        !teacherDropdownRef.current.contains(event.target) &&
        (!panelElement || !panelElement.contains(event.target))
      ) {
        setIsTeacherDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTeacherDropdownOpen]);

  React.useEffect(() => {
    if (!isGroupDropdownOpen) {
      setGroupSearchTerm('');
      return;
    }

    const handleClickOutside = (event) => {
      const panelElement = document.getElementById('group-filter-panel');
      if (
        groupDropdownRef.current &&
        !groupDropdownRef.current.contains(event.target) &&
        (!panelElement || !panelElement.contains(event.target))
      ) {
        setIsGroupDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isGroupDropdownOpen]);

  React.useLayoutEffect(() => {
    if (!isTeacherDropdownOpen) {
      return;
    }
    updateTeacherPanelPosition();
    const handleReposition = () => updateTeacherPanelPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isTeacherDropdownOpen, updateTeacherPanelPosition]);

  React.useLayoutEffect(() => {
    if (!isGroupDropdownOpen) {
      return;
    }
    updateGroupPanelPosition();
    const handleReposition = () => updateGroupPanelPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isGroupDropdownOpen, updateGroupPanelPosition]);

  const handleDesktopGroupSelect = (groupName) => {
    const value =
      groupName == null || groupName === ''
        ? (isStudentsBuild() ? SCHEDULE_FILTER_ALL_GROUPS : '')
        : groupName;
    handleGroupsChange(value);
    setIsGroupDropdownOpen(false);
  };

  const teacherDropdownPanel = isTeacherDropdownOpen ? (
    <div
      id="teacher-filter-panel"
      style={teacherPanelStyle}
      className="bg-white shadow-xl rounded-xl border border-gray-200 max-h-72 overflow-hidden flex flex-col"
    >
      <div className="p-2 border-b border-gray-100 bg-white">
        <input
          type="text"
          value={teacherSearchTerm}
          onChange={(e) => setTeacherSearchTerm(e.target.value)}
          placeholder="Search teachers..."
          className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
          autoFocus
          aria-label="Search teachers"
        />
      </div>
      <ul className="py-1 overflow-y-auto" role="listbox" aria-label="Teacher options">
        <li
          className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer"
          onMouseDown={(e) => {
            e.preventDefault();
            handleDesktopTeacherSelect('');
          }}
          role="option"
          aria-selected={!selectedTeacher}
        >
          All Teachers
        </li>
        {filteredTeacherNames.length > 0 ? filteredTeacherNames.map((teacherName, index) => (
          <li
            key={`${teacherName}-${index}`}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer"
            onMouseDown={(e) => {
              e.preventDefault();
              handleDesktopTeacherSelect(teacherName);
            }}
            role="option"
            aria-selected={selectedTeacher === teacherName}
          >
            {teacherName}
          </li>
        )) : (
          <li className="px-3 py-2 text-sm text-gray-400 italic">No teachers found</li>
        )}
      </ul>
    </div>
  ) : null;

  const groupDropdownPanel = isGroupDropdownOpen ? (
    <div
      id="group-filter-panel"
      style={groupPanelStyle}
      className="bg-white shadow-xl rounded-xl border border-gray-200 max-h-72 overflow-hidden flex flex-col"
    >
      <div className="p-2 border-b border-gray-100 bg-white">
        <input
          type="text"
          value={groupSearchTerm}
          onChange={(e) => setGroupSearchTerm(e.target.value)}
          placeholder="Search groups..."
          className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
          autoFocus
          aria-label="Search groups"
        />
      </div>
      <ul className="py-1 overflow-y-auto" role="listbox" aria-label="Group options">
        <li
          className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer"
          onMouseDown={(e) => {
            e.preventDefault();
            handleDesktopGroupSelect(SCHEDULE_FILTER_ALL_GROUPS);
          }}
          role="option"
          aria-selected={!selectedGroups?.[0] || selectedGroups[0] === SCHEDULE_FILTER_ALL_GROUPS}
        >
          All Groups
        </li>
        {filteredGroupNames.length > 0 ? filteredGroupNames.map((groupName, index) => (
          <li
            key={`${groupName}-${index}`}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer"
            onMouseDown={(e) => {
              e.preventDefault();
              handleDesktopGroupSelect(groupName);
            }}
            role="option"
            aria-selected={selectedGroups?.[0] === groupName}
          >
            {groupName}
          </li>
        )) : (
          <li className="px-3 py-2 text-sm text-gray-400 italic">No groups found</li>
        )}
      </ul>
    </div>
  ) : null;

  return (
    <>
      {/* Mobile View - phone-only compact filter buttons. */}
      <div className={isMobileLayout ? 'block' : 'hidden'}>
        <MobileFilterButtons
          selectedTeacher={selectedTeacher}
          selectedGroups={selectedGroups}
          onOpenStudentModal={handleOpenStudentModal}
          onOpenTeacherModal={handleOpenTeacherModal}
          teachers={teacherOptions}
          groups={groupOptions}
          showTeacher={showTeacher}
          showStudent={showGroups && !(isPublicView && isStudentsBuild())}
          compact={compactMobile}
        />
      </div>

      {/* Desktop View */}
      <div className={`${isMobileLayout ? 'hidden' : 'flex'} w-full items-center justify-center gap-3`}>
        {/* Teacher Filter - Label Removed */}
        {showTeacher && (
          <div className="w-[250px]">
            {complexFiltersActive ? (
              <div className={modernReadonlyControlClass}>
                {getComplexFilterText()}
              </div>
            ) : (
              <div className="relative" ref={teacherDropdownRef}>
                <button
                  id="teacher-filter"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsGroupDropdownOpen(false);
                    if (!isTeacherDropdownOpen) {
                      updateTeacherPanelPosition();
                    }
                    setIsTeacherDropdownOpen(prev => !prev);
                  }}
                  className={modernFilterButtonClass}
                  aria-haspopup="listbox"
                  aria-expanded={isTeacherDropdownOpen}
                  aria-label="Filter by Teacher"
                  ref={teacherTriggerRef}
                >
                  <span className="truncate">{selectedTeacher || 'All Teachers'}</span>
                  <span className={`text-gray-400 transition-transform ${isTeacherDropdownOpen ? 'rotate-180' : ''}`}>
                    <ChevronDownIcon />
                  </span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Course Filter - Only show for admin view */}
        {showCourse && (
          <div className="w-[250px]">
            <div className="relative">
              <select
                id="course-filter"
                value={selectedCourse || ''}
                onChange={handleCourseChange}
                className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base min-h-[48px] appearance-none"
                aria-label="Filter by Course"
              >
                <option value="">All Courses</option>
                {courseOptions.filter(c => c).map(course => {
                  const courseName = typeof course === 'string' ? course : course?.name || 'Unknown';
                  const courseKey = typeof course === 'string' ? course : course?.id || course?.name || 'Unknown';
                  return <option key={courseKey} value={courseName}>{courseName}</option>;
                })}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDownIcon />
              </div>
            </div>
          </div>
        )}

        {/* Group Filter - single-select for public or student app; multi only for admin editing */}
        {showGroups && (
          <div className="w-[250px]">
            {useSingleSelectGroupFilter ? (
              <div className="relative" ref={groupDropdownRef}>
                <button
                  id="group-filter"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsTeacherDropdownOpen(false);
                    if (!isGroupDropdownOpen) {
                      updateGroupPanelPosition();
                    }
                    setIsGroupDropdownOpen(prev => !prev);
                  }}
                  className={modernFilterButtonClass}
                  aria-haspopup="listbox"
                  aria-expanded={isGroupDropdownOpen}
                  aria-label="Filter by Group"
                  ref={groupTriggerRef}
                >
                  <span className="truncate">
                    {!selectedGroups?.[0] || selectedGroups[0] === SCHEDULE_FILTER_ALL_GROUPS
                      ? 'All Groups'
                      : selectedGroups[0]}
                  </span>
                  <span className={`text-gray-400 transition-transform ${isGroupDropdownOpen ? 'rotate-180' : ''}`}>
                    <ChevronDownIcon />
                  </span>
                </button>
              </div>
            ) : (
              <>
                <span id="group-filter-label" className="sr-only">Filter by Group(s)</span>
                <MultiSelectDropdown
                  options={groupOptions.filter(g => g).map(group => typeof group === 'string' ? group : group?.name || 'Unknown')}
                  selectedOptions={selectedGroups}
                  onChange={handleGroupsChange}
                  placeholder="All Groups"
                  aria-labelledby="group-filter-label"
                />
              </>
            )}
          </div>
        )}

        {/* Action Buttons - Pushed to the right */}
        <div className="flex items-center gap-2">
          {/* Complex Filter Button - Hide only for students view */}
          {!isStudentsBuild() && (
            <button
              onClick={handleOpenComplexFilter}
              className="p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded-full"
              aria-label="Open advanced filters"
              title="Advanced Filters"
            >
              <HiAdjustmentsHorizontal className="h-5 w-5" />
            </button>
          )}

          {/* Clear Button */}
          <button
            onClick={handleClearFilters}
            className="p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded-full"
            aria-label="Clear all filters"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Teacher Desktop Dropdown Panel */}
      {teacherDropdownPanel && createPortal(teacherDropdownPanel, document.body)}

      {groupDropdownPanel && createPortal(groupDropdownPanel, document.body)}

      <TeacherSelectionModal
        isOpen={isTeacherModalOpen}
        onClose={handleCloseTeacherModal}
        teachers={teacherOptions}
        selectedTeacher={selectedTeacher}
        onTeacherSelect={handleTeacherSelect}
      />
    </>
  );
}

export default ScheduleFilter;